import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/store";
import { toast } from "react-toastify";

import { throwApiError } from "../../../api/api";
import {
  updateFreelancerMeApi,
  type FreelancerPublic,
  type UpdateFreelancerRequest,
} from "../../../views/freelancer/freelancerApi";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  freelancer: FreelancerPublic | null;
  onUpdated?: (updated: FreelancerPublic) => void;
};

const onlyDigits = (value: string) => value.replace(/\D/g, "");

const trimOrEmpty = (v: string) => v.trim();

const patchField = (nextRaw: string, prevValue?: string | null) => {
  const prev = (prevValue ?? "").trim();
  const next = nextRaw.trim();

  if (next === prev) return undefined;
  if (next === "" && prev !== "") return "";
  if (next !== "" && next !== prev) return next;
  return undefined;
};

const EditFreelancerModal: React.FC<Props> = ({ isOpen, onClose, freelancer, onUpdated }) => {
  const [nomeCompleto, setNomeCompleto] = useState("");
  const [cpf, setCpf] = useState("");
  const [contato, setContato] = useState("");
  const [pix, setPix] = useState("");
  const [banco, setBanco] = useState("");

  const [rua, setRua] = useState("");
  const [numero, setNumero] = useState("");
  const [complemento, setComplemento] = useState("");
  const [bairro, setBairro] = useState("");
  const [cidade, setCidade] = useState("");
  const [estado, setEstado] = useState("");

  const token = useSelector((state: RootState) => state.freelancerAuth.accessToken);

  // Saving
  const [saving, setSaving] = useState(false);

  const formErrors = useMemo(() => {
    const errors: string[] = [];

    if (!freelancer) errors.push("Não foi possível carregar seus dados.");

    if (!trimOrEmpty(nomeCompleto)) errors.push("Informe o nome completo.");

    const cpfDigits = onlyDigits(cpf);
    if (!cpfDigits || cpfDigits.length !== 11) errors.push("CPF inválido (deve ter 11 dígitos).");

    return errors;
  }, [freelancer, nomeCompleto, cpf]);

  const canSubmit = useMemo(() => {
    if (saving) return false;
    return formErrors.length === 0;
  }, [saving, formErrors]);

  // Fecha com ESC + trava scroll quando aberto (mesmo padrão)
  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose, saving]);

  // Preenche campos ao abrir (ou quando freelancer mudar)
  useEffect(() => {
    if (!isOpen) return;

    setSaving(false);

    setNomeCompleto(freelancer?.nomeCompleto ?? "");
    setCpf(freelancer?.cpf ?? "");

    setContato(freelancer?.contato ?? "");
    setPix(freelancer?.pix ?? "");
    setBanco(freelancer?.banco ?? "");

    setRua(freelancer?.rua ?? "");
    setNumero(freelancer?.numero ?? "");
    setComplemento(freelancer?.complemento ?? "");
    setBairro(freelancer?.bairro ?? "");
    setCidade(freelancer?.cidade ?? "");
    setEstado(freelancer?.estado ?? "");
  }, [isOpen, freelancer]);

  const handleOverlayClick = () => {
    if (!saving) onClose();
  };

  const handleModalClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    if (formErrors.length > 0) {
      toast.error(formErrors[0]);
      return;
    }
    if (!freelancer) return;

    const cpfDigits = onlyDigits(cpf);

    const dto: UpdateFreelancerRequest = {};

    // Nome/CPF (normalmente não seria legal “apagar”; aqui tratamos apenas mudança)
    if (trimOrEmpty(nomeCompleto) !== (freelancer.nomeCompleto ?? "").trim()) {
      dto.nomeCompleto = trimOrEmpty(nomeCompleto);
    }
    if (cpfDigits !== (freelancer.cpf ?? "").trim()) {
      dto.cpf = cpfDigits;
    }

    // Contato / Pagamento (permite limpar)
    const contatoPatch = patchField(contato, freelancer.contato);
    if (contatoPatch !== undefined) dto.contato = contatoPatch;

    const pixPatch = patchField(pix, freelancer.pix);
    if (pixPatch !== undefined) dto.pix = pixPatch;

    const bancoPatch = patchField(banco, freelancer.banco);
    if (bancoPatch !== undefined) dto.banco = bancoPatch;

    // Endereço (permite limpar)
    const ruaPatch = patchField(rua, freelancer.rua);
    if (ruaPatch !== undefined) dto.rua = ruaPatch;

    const numeroPatch = patchField(numero, freelancer.numero);
    if (numeroPatch !== undefined) dto.numero = numeroPatch;

    const complementoPatch = patchField(complemento, freelancer.complemento);
    if (complementoPatch !== undefined) dto.complemento = complementoPatch;

    const bairroPatch = patchField(bairro, freelancer.bairro);
    if (bairroPatch !== undefined) dto.bairro = bairroPatch;

    const cidadePatch = patchField(cidade, freelancer.cidade);
    if (cidadePatch !== undefined) dto.cidade = cidadePatch;

    const estadoPatch = patchField(estado, freelancer.estado);
    if (estadoPatch !== undefined) dto.estado = estadoPatch;

    if (Object.keys(dto).length === 0) {
      toast.info("Nenhuma alteração para salvar.");
      onClose();
      return;
    }

    try {
      setSaving(true);

      const updated = await updateFreelancerMeApi(token!, dto);

      toast.success("Dados atualizados com sucesso.");
      onUpdated?.(updated);
      onClose();
    } catch (error) {
      throwApiError(error, "Erro ao atualizar freelancer.");
      toast.error("Erro ao atualizar freelancer.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="ModalOverlay" onClick={handleOverlayClick} role="presentation">
      <div className="ModalShell" onClick={handleModalClick} role="dialog" aria-modal="true">
        <h2 className="ModalTitle">Editar minhas informações</h2>

        <form className="ModalBody" onSubmit={handleSubmit}>
          <div className="Box">
            <div className="BoxTitle">Dados pessoais</div>

            <div className="ModalGrid2">
              <div className="ModalField">
                <label className="ModalLabel" htmlFor="nomeCompleto">
                  Nome completo
                </label>
                <input
                  id="nomeCompleto"
                  className="ModalInput"
                  value={nomeCompleto}
                  onChange={(e) => setNomeCompleto(e.target.value)}
                  disabled={saving || !freelancer}
                  placeholder="Ex.: Maria José"
                />
              </div>

              <div className="ModalField">
                <label className="ModalLabel" htmlFor="cpf">
                  CPF
                </label>
                <input
                  id="cpf"
                  className="ModalInput"
                  value={cpf}
                  onChange={(e) => setCpf(e.target.value)}
                  disabled={saving || !freelancer}
                  placeholder="Somente números"
                  inputMode="numeric"
                />
              </div>

              <div className="ModalField">
                <label className="ModalLabel" htmlFor="contato">
                  Contato (WhatsApp / Telefone)
                </label>
                <input
                  id="contato"
                  className="ModalInput"
                  value={contato}
                  onChange={(e) => setContato(e.target.value)}
                  disabled={saving || !freelancer}
                  placeholder="Ex.: (11) 99999-9999"
                  inputMode="tel"
                  autoComplete="tel"
                />
              </div>
            </div>
          </div>

          <div className="Box">
            <div className="BoxTitle">Pagamento</div>

            <div className="ModalGrid2">
              <div className="ModalField">
                <label className="ModalLabel" htmlFor="pix">
                  Chave Pix
                </label>
                <input
                  id="pix"
                  className="ModalInput"
                  value={pix}
                  onChange={(e) => setPix(e.target.value)}
                  disabled={saving || !freelancer}
                  placeholder="CPF, e-mail, telefone ou aleatória"
                />
              </div>

              <div className="ModalField">
                <label className="ModalLabel" htmlFor="banco">
                  Banco
                </label>
                <input
                  id="banco"
                  className="ModalInput"
                  value={banco}
                  onChange={(e) => setBanco(e.target.value)}
                  disabled={saving || !freelancer}
                  placeholder="Ex.: Nubank, Itaú, Bradesco..."
                />
              </div>
            </div>
          </div>

          <div className="Box">
            <div className="BoxTitle">Endereço</div>

            <div className="ModalGrid2">
              <div className="ModalField">
                <label className="ModalLabel" htmlFor="rua">
                  Rua
                </label>
                <input
                  id="rua"
                  className="ModalInput"
                  value={rua}
                  onChange={(e) => setRua(e.target.value)}
                  disabled={saving || !freelancer}
                  placeholder="Ex.: Av. Paulista"
                />
              </div>

              <div className="ModalField">
                <label className="ModalLabel" htmlFor="numero">
                  Número
                </label>
                <input
                  id="numero"
                  className="ModalInput"
                  value={numero}
                  onChange={(e) => setNumero(e.target.value)}
                  disabled={saving || !freelancer}
                  placeholder="Ex.: 1000"
                  inputMode="numeric"
                />
              </div>

              <div className="ModalField">
                <label className="ModalLabel" htmlFor="complemento">
                  Complemento
                </label>
                <input
                  id="complemento"
                  className="ModalInput"
                  value={complemento}
                  onChange={(e) => setComplemento(e.target.value)}
                  disabled={saving || !freelancer}
                  placeholder="Ex.: Apto 12, Bloco B"
                />
              </div>

              <div className="ModalField">
                <label className="ModalLabel" htmlFor="bairro">
                  Bairro
                </label>
                <input
                  id="bairro"
                  className="ModalInput"
                  value={bairro}
                  onChange={(e) => setBairro(e.target.value)}
                  disabled={saving || !freelancer}
                  placeholder="Ex.: Centro"
                />
              </div>

              <div className="ModalField">
                <label className="ModalLabel" htmlFor="cidade">
                  Cidade
                </label>
                <input
                  id="cidade"
                  className="ModalInput"
                  value={cidade}
                  onChange={(e) => setCidade(e.target.value)}
                  disabled={saving || !freelancer}
                  placeholder="Ex.: São Paulo"
                />
              </div>

              <div className="ModalField">
                <label className="ModalLabel" htmlFor="estado">
                  Estado (UF)
                </label>
                <select
                  id="estado"
                  className="ModalInput"
                  value={estado}
                  onChange={(e) => setEstado(e.target.value)}
                  disabled={saving || !freelancer}
                >
                  <option value="">Selecione</option>
                  <option value="RJ">RJ</option>
                  <option value="SP">SP</option>
                </select>
              </div>
            </div>
          </div>

          <div className="ModalActions">
            <button
              type="button"
              className="ModalButton ModalButtonCancel"
              onClick={onClose}
              disabled={saving}
            >
              Cancelar
            </button>

            <button
              type="submit"
              className="ModalButton ModalButtonConfirm"
              disabled={!canSubmit}
              title={formErrors.length > 0 ? formErrors[0] : ""}
            >
              {saving ? "Salvando..." : "Salvar alterações"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditFreelancerModal;
